/* eslint-disable @typescript-eslint/no-unnecessary-condition --
   Proto types fields as required but the BE JSON may omit them; guards trigger the fetch fallback. */
/**
 * Payload-inventory seed for the PL bundle buy box: converts per-child `activeInventory` so the legacy
 * mount-time fan-out (one request per product) is skipped — the bundle twin of `productPurchaseInfoSeed`.
 * All-or-nothing over required children + add-ons (merge siblings excluded; sibling swaps still fetch);
 * returns `undefined` to fall back to the client fan-out. BE currently sends `{}`, so it's inert today.
 */
import type Product from '@src/quince-site-common/src/domains/Product'
import type { IProductPurchaseInfo } from '@src/quince-site-common/src/domains/ProductPurchaseInfo'
import type { InventoryResponse } from '@src/quince-site-common/src/services/inventoryService/inventory.service'
import productService from '@src/quince-site-common/src/services/productService'

import type {
  BundlePurchaseWidgetData,
  VariantActiveInventory,
} from '@lastbrand/storefront-proto-registry/api/v1/widget'

/** What the buy box's `UPDATE_PRICING_INFO_FOR_ALL` reducer expects: keyed by child `product.id`. */
export type BundlePurchaseInfoSeed = Record<number, IProductPurchaseInfo>

export interface BundleInventorySeed {
  /** Per-child purchase info, keyed by `product.id` — matches `getProductsPurchaseInfo`'s return. */
  purchaseInfo: BundlePurchaseInfoSeed
  /** Union of every child's inventory, for the `inventory_data` sessionStorage contract. */
  inventoryMap: Record<string, InventoryResponse>
}

// One product's `activeInventory` → `/active-inventory` response shape; `undefined` when any variant
// lacks usable data. `locationId`/`vendorCode` are unused downstream, so defaulted.
function toInventoryMap(
  activeInventory: Record<string, VariantActiveInventory> | undefined,
  variantIds: readonly number[],
): Record<string, InventoryResponse> | undefined {
  if (!activeInventory || variantIds.length === 0) return undefined

  const map: Record<string, InventoryResponse> = {}
  for (const variantId of variantIds) {
    const entry = activeInventory[String(variantId)]
    if (entry?.available == null || !entry.inventoryType) return undefined
    map[String(variantId)] = {
      available: entry.available,
      inventoryType: entry.inventoryType,
      tags: entry.tags?.preOrderShippingDate ? { preOrderShippingDate: entry.tags.preOrderShippingDate } : null,
      isInventoryPresentAtSFWarehouse: entry.isInventoryPresentAtSFWarehouse,
      locationId: 0,
      vendorCode: '',
    }
  }
  return map
}

/** Build the initial-load seed, or `undefined` to fall back to the client fan-out. */
export function getBundleInventorySeed(
  data: BundlePurchaseWidgetData,
  products: readonly Product[],
): BundleInventorySeed | undefined {
  // Required children + add-ons only — requiring merge-sibling inventory would defeat the seed.
  const items = [...(data.customBundleProducts ?? []), ...(data.addOnBundleProducts ?? [])]
  if (items.length === 0) return undefined

  // `buildProductPurchaseInfo` needs the domain `Product`; `activeInventory` lives on the proto item.
  const productById = new Map<string, Product>(products.map(product => [String(product.id), product]))

  const purchaseInfo: BundlePurchaseInfoSeed = {}
  const inventoryMap: Record<string, InventoryResponse> = {}

  for (const item of items) {
    const product = productById.get(String(item.id))
    if (!product) return undefined

    const variantIds = (product.variants ?? []).map(variant => variant.id).filter((id): id is number => id != null)
    const itemInventory = toInventoryMap(item.activeInventory, variantIds)
    if (!itemInventory) return undefined

    Object.assign(inventoryMap, itemInventory)
    // Fetch-free twin of `getProductPurchaseInfo`, so seeded and fetched inventory can't drift.
    purchaseInfo[Number(product.id)] = productService.buildProductPurchaseInfo(product, itemInventory)
  }

  return { purchaseInfo, inventoryMap }
}

/* eslint-enable @typescript-eslint/no-unnecessary-condition */
